/**
 * ServiceRequestsTracking — iter-249 (Feb 2026)
 * =============================================
 * Live board / table of every company_service_request across the pipeline.
 * Filters: stage, company, free-text search. Click a row to open details.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import {
  Loader2, Filter, Search, Plus,
  ClipboardList, ChevronRight,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { useLanguage } from '../../context/LanguageContext';
import { getAuthHeaders } from '../../utils/authHeaders';
import { AgentPageShell } from '../agent/AgentPageShell';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STAGE_META = {
  REQUEST_RECEIVED:         { ar: 'تم استلام الطلب',       en: 'Received',       color: 'blue'    },
  REQUEST_ACCEPTED:         { ar: 'تم قبول الطلب',         en: 'Accepted',       color: 'sky'     },
  IN_PROGRESS:              { ar: 'جاري تنفيذ الطلب',     en: 'In Progress',    color: 'amber'   },
  FEE_COLLECTION:           { ar: 'تحصيل الرسوم',           en: 'Fee Collection', color: 'orange'  },
  COMPLETED_AND_DISPATCHED: { ar: 'تم الانتهاء والإرسال', en: 'Completed',      color: 'emerald' },
};
const STAGE_PILL = {
  blue:    'bg-blue-500/20 text-blue-300 border-blue-500/30',
  sky:     'bg-sky-500/20 text-sky-300 border-sky-500/30',
  amber:   'bg-amber-500/20 text-amber-300 border-amber-500/30',
  orange:  'bg-orange-500/20 text-orange-300 border-orange-500/30',
  emerald: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30',
};

export default function ServiceRequestsTracking() {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const isAr = language === 'ar';
  const t = (ar, en) => (isAr ? ar : en);

  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stageFilter, setStageFilter] = useState('');
  const [search, setSearch] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (stageFilter) params.set('status', stageFilter);
      if (search.trim()) params.set('q', search.trim());
      const r = await axios.get(`${API}/admin/company-service-requests?${params}`, { headers: getAuthHeaders() });
      setRequests(r.data?.data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [stageFilter]);

  const counts = useMemo(() => {
    const out = { total: requests.length };
    Object.keys(STAGE_META).forEach((k) => { out[k] = 0; });
    requests.forEach((r) => { if (out[r.status] !== undefined) out[r.status] += 1; });
    return out;
  }, [requests]);

  const breadcrumb = [
    { label_ar: 'الرئيسية', label_en: 'Home', path: '/dashboard' },
    { label_ar: 'CLOUDGM FZ LLC (للإشراف)', label_en: 'CLOUDGM FZ LLC (Supervision)', path: '/dashboard?platform=fzlc' },
    { label_ar: 'Cloud GM Company Agent', label_en: 'Cloud GM Company Agent', path: '/manage/cloudgm-agent' },
    { label_ar: 'خدمات الشركات المسجلة', label_en: 'Registered Companies Services', path: '/registered-companies-services' },
    { label_ar: 'متابعة الطلبات', label_en: 'Tracking', path: null },
  ];

  return (
    <AgentPageShell breadcrumb={breadcrumb} onBack={() => navigate('/registered-companies-services')}>
      <div className="max-w-7xl mx-auto">
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
          <div className="flex items-center justify-between mb-6 gap-3 flex-wrap">
            <div className="flex items-center gap-3">
              <div className="h-11 w-11 rounded-xl bg-cyan-500/10 border border-cyan-500/30 flex items-center justify-center">
                <ClipboardList className="h-5 w-5 text-cyan-400" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-white" data-testid="tracking-title">
                  {t('متابعة الطلبات', 'Order Tracking & Pipeline')}
                </h1>
                <p className="text-[12px] text-white/50">
                  {t('لوحة مباشرة لكل طلبات الخدمة عبر مراحل الدورة.',
                     'Live board of every service request across the pipeline.')}
                </p>
              </div>
            </div>
            <Button onClick={() => navigate('/registered-companies-services/new')}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl"
                    data-testid="tracking-new-btn">
              <Plus className="h-4 w-4 me-1" />
              {t('طلب جديد', 'New Request')}
            </Button>
          </div>

          {/* Stage counters */}
          <div className="grid grid-cols-2 md:grid-cols-6 gap-2 mb-4">
            <button
              onClick={() => setStageFilter('')}
              className={`px-3 py-2 rounded-xl border text-[12px] text-center transition-all
                ${!stageFilter ? 'bg-white/10 border-white/30 text-white' : 'bg-black/20 border-white/5 text-white/60 hover:bg-white/5'}`}
              data-testid="tracking-filter-all"
            >
              <div className="font-bold text-base">{counts.total}</div>
              <div>{t('الكل', 'All')}</div>
            </button>
            {Object.entries(STAGE_META).map(([key, meta]) => (
              <button
                key={key}
                onClick={() => setStageFilter(stageFilter === key ? '' : key)}
                className={`px-3 py-2 rounded-xl border text-[11px] text-center transition-all
                  ${stageFilter === key ? STAGE_PILL[meta.color] : 'bg-black/20 border-white/5 text-white/60 hover:bg-white/5'}`}
                data-testid={`tracking-filter-${key}`}
              >
                <div className="font-bold text-base">{counts[key] || 0}</div>
                <div className="truncate">{isAr ? meta.ar : meta.en}</div>
              </button>
            ))}
          </div>

          {/* Search */}
          <div className="flex items-center gap-2 mb-4">
            <div className="relative flex-1">
              <Search className="h-4 w-4 text-white/40 absolute top-1/2 -translate-y-1/2 start-3" />
              <Input value={search} onChange={(e) => setSearch(e.target.value)}
                     onKeyDown={(e) => { if (e.key === 'Enter') load(); }}
                     placeholder={t('ابحث بالشركة / الخدمة / رقم الطلب…', 'Search company / service / request no…')}
                     className="bg-[#0a0a0a] border-white/10 text-white ps-9 h-10"
                     dir={isAr ? 'rtl' : 'ltr'}
                     data-testid="tracking-search" />
            </div>
            <Button onClick={load} variant="outline" className="border-white/10 text-white h-10"
                    data-testid="tracking-search-btn">
              <Filter className="h-4 w-4 me-1" />
              {t('تطبيق', 'Apply')}
            </Button>
          </div>

          {loading ? (
            <div className="text-center py-16"><Loader2 className="h-6 w-6 animate-spin mx-auto text-emerald-400" /></div>
          ) : requests.length === 0 ? (
            <div className="text-center py-16 border border-dashed border-white/10 rounded-2xl bg-black/30 text-white/50"
                 data-testid="tracking-empty">
              {t('لا توجد طلبات', 'No requests found')}
            </div>
          ) : (
            <div className="overflow-x-auto rounded-2xl border border-white/10 bg-white/[0.02]">
              <table className="w-full text-[12px]">
                <thead className="bg-black/40 text-white/50">
                  <tr>
                    <th className="text-start px-3 py-2 font-medium">{t('رقم الطلب', 'Request')}</th>
                    <th className="text-start px-3 py-2 font-medium">{t('الشركة', 'Company')}</th>
                    <th className="text-start px-3 py-2 font-medium">{t('الخدمة', 'Service')}</th>
                    <th className="text-start px-3 py-2 font-medium">{t('السعر', 'Price')}</th>
                    <th className="text-start px-3 py-2 font-medium">{t('المرحلة', 'Stage')}</th>
                    <th className="text-start px-3 py-2 font-medium">{t('الفاتورة', 'Invoice')}</th>
                    <th className="text-start px-3 py-2 font-medium">{t('التاريخ', 'Date')}</th>
                    <th className="px-3 py-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {requests.map((r) => {
                    const meta = STAGE_META[r.status] || { ar: r.status, en: r.status, color: 'blue' };
                    return (
                      <tr key={r.id}
                          onClick={() => navigate(`/registered-companies-services/${r.id}`)}
                          className="border-t border-white/5 hover:bg-white/[0.04] cursor-pointer"
                          data-testid={`tracking-row-${r.id}`}>
                        <td className="px-3 py-2 font-mono text-white/80">
                          {r.request_no}
                          {r.source === 'merchant_platform' && (
                            <span className="ms-2 inline-block text-[10px] px-1.5 py-0.5 rounded-md bg-sky-500/15 border border-sky-500/40 text-sky-300 font-sans" data-testid={`csr-source-merchant-${r.id}`}>
                              {isAr ? 'من منصة التجار' : 'From merchants platform'}
                            </span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-white/80 truncate max-w-[220px]">
                          {isAr ? (r.company_name_ar || r.company_name_en) : (r.company_name_en || r.company_name_ar)}
                        </td>
                        <td className="px-3 py-2 text-white/80 truncate max-w-[220px]">
                          {isAr ? (r.main_service_name_ar || r.main_service_name_en) : (r.main_service_name_en || r.main_service_name_ar)}
                          {r.sub_service_name_ar && (
                            <span className="text-white/50 mx-1">·</span>
                          )}
                          <span className="text-white/50">
                            {isAr ? (r.sub_service_name_ar || '') : (r.sub_service_name_en || '')}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-emerald-300 font-semibold">
                          {Number(r.price || 0).toLocaleString(isAr ? 'ar-EG' : 'en-US', { minimumFractionDigits: 2 })} AED
                        </td>
                        <td className="px-3 py-2">
                          <span className={`px-2 py-0.5 rounded border text-[10px] ${STAGE_PILL[meta.color]}`}>
                            {isAr ? meta.ar : meta.en}
                          </span>
                        </td>
                        <td className="px-3 py-2 font-mono text-white/60">
                          {r.invoice_no_display || '—'}
                        </td>
                        <td className="px-3 py-2 text-white/60">
                          {r.created_at ? new Date(r.created_at).toLocaleDateString(isAr ? 'ar-EG' : 'en-GB') : '—'}
                        </td>
                        <td className="px-3 py-2 text-white/40">
                          <ChevronRight className={`h-4 w-4 ${isAr ? 'rotate-180' : ''}`} />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </motion.div>
      </div>
    </AgentPageShell>
  );
}
